import { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Sparkles, ListChecks } from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";

const GLOBAL_SUGGESTIONS = [
  "¿Qué documentos necesito para la licencia de obra?",
  "¿Qué es el libro del edificio?",
  "¿Cómo funciona la hipoteca autopromotor?",
  "¿Qué diferencia hay entre arquitecto y aparejador?",
];

const PHASE_SUGGESTIONS = {
  terreno_viabilidad: ["¿Qué es la cédula urbanística?", "¿Para qué sirve el estudio geotécnico?"],
  equipo_tecnico: ["¿Qué hace la Dirección Facultativa?", "¿Cuánto cobra un arquitecto por un chalet?"],
  anteproyecto_basico: ["¿Qué incluye el Proyecto Básico?", "¿Cuánto tarda una licencia de obra?"],
  ejecucion_licencia: ["¿Qué es el ICIO y cuánto se paga?", "¿Qué es el Proyecto de Ejecución?"],
  financiacion_bancaria: ["¿Cómo son las certificaciones de obra?", "¿Qué es la Escritura de Obra Nueva?"],
  licitacion_constructora: ["¿Qué debe incluir el contrato de obra?", "¿Qué es la retención del 5%?"],
  construccion_obra: ["¿Qué es el Acta de Replanteo?", "¿Qué es el Libro de Órdenes?"],
  final_obra_tramites: ["¿Qué es el Certificado Final de Obra?", "¿Qué trámites hay al acabar la obra?"],
  primera_ocupacion_habitar: ["¿Qué es la Licencia de Primera Ocupación?", "¿Cómo doy de alta la luz y el agua?"],
};

export default function MoriAIChat({ open, onOpenChange, activePhase, projectId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    setMessages([]);
  }, [projectId]);

  useEffect(() => {
    if (!open || loaded || !projectId) return;
    api
      .get(`/projects/${projectId}/chat/history`)
      .then((r) => setMessages(r.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, loaded, projectId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, draft, streaming]);

  const suggestions = activePhase
    ? [...(PHASE_SUGGESTIONS[activePhase.id] || []), ...GLOBAL_SUGGESTIONS].slice(0, 4)
    : GLOBAL_SUGGESTIONS;

  const send = async (text, phaseId) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message, message_id: `local_${Date.now()}` }]);
    setStreaming(true);
    setDraft("");
    try {
      const resp = await fetch(`${API}/projects/${projectId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(phaseId ? { message, phase_id: phaseId } : { message }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setDraft(full);
      }
      setMessages((m) => [...m, { role: "assistant", content: full, message_id: `local_a_${Date.now()}` }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Lo siento, ha ocurrido un error. Inténtalo de nuevo.", message_id: `err_${Date.now()}` },
      ]);
    } finally {
      setDraft("");
      setStreaming(false);
    }
  };

  const empty = messages.length === 0 && !streaming;

  const phaseSummary = () => {
    if (!activePhase || streaming) return;
    send(
      `Hazme un resumen de la fase "${activePhase.name}" y dime, en pasos claros, qué debería hacer ahora teniendo en cuenta las tareas que aún tengo pendientes.`,
      activePhase.id,
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="mori-chat-panel">
        <SheetHeader className="px-5 py-4 border-b border-border/70 text-left">
          <SheetTitle className="flex items-center gap-2.5">
            <img src="/mori-logo.jpg" alt="Mori" className="h-9 w-9 rounded-lg object-cover" />
            <div>
              <div className="font-bold">Mori</div>
              <div className="text-xs font-normal text-muted-foreground">Tu asistente de autopromoción</div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {empty && (
            <div className="pt-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Sparkles className="h-4 w-4 text-primary" />
                Pregúntame cualquier duda del proceso.
              </div>
              <div className="space-y-2">
                {suggestions.map((q, i) => (
                  <button
                    key={q}
                    data-testid={`suggested-question-${i}`}
                    onClick={() => send(q)}
                    className="w-full text-left text-sm rounded-xl border border-border bg-card px-4 py-3 hover:border-primary hover:bg-accent transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={m.message_id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`chat-message-${i}`}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-background-subtle border border-border/60 rounded-bl-sm"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}

          {streaming && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-background-subtle border border-border/60" data-testid="chat-streaming">
                {draft || (
                  <span className="mori-typing inline-flex items-center">
                    <span /> <span /> <span />
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border/70 p-4">
          {activePhase && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={phaseSummary}
              disabled={streaming}
              data-testid="phase-summary-button"
              className="rounded-full gap-1.5 mb-3 w-full border-[#C75B28]/40 text-[#C75B28] hover:bg-[#C75B28]/10 hover:text-[#C75B28]"
            >
              <ListChecks className="h-4 w-4" /> Resumen de la fase: {activePhase.name}
            </Button>
          )}
          <div className="flex items-end gap-2">
            <Textarea
              data-testid="chat-input-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Escribe tu pregunta…"
              rows={1}
              className="resize-none min-h-[44px] max-h-32"
            />
            <Button
              data-testid="chat-submit-button"
              onClick={() => send()}
              disabled={streaming || !input.trim()}
              size="icon"
              className="rounded-full h-11 w-11 shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 text-center">
            Mori es orientativo. Confirma los detalles legales y técnicos con tu arquitecto y tu ayuntamiento.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
